import axios from "./axios";
import { ApiResponse } from "../types/express-data.type";
import {
  DailyExpenseBulkApproveResult,
  DailyExpenseDay,
  DailyExpenseMonth,
  DailyExpenseReview,
  DailyExpenseReviewParams,
  DailyExpenseSaveRequest,
  ExpenseCategoryList,
} from "../types/daily-expense.type";

const EXPENSE_CATEGORIES_PATH = "/expense-categories";
const DAILY_EXPENSES_PATH = "/daily-expenses";

/** The categories the branch keys every day; the monthly ones belong to head office's month close. */
const DAILY_ENTRY_TYPE = "DAILY";

/**
 * A refusal on a write (a closed month, a document head office already took) is the server's
 * Thai message in the global toast; the page stays as it is, so the 403 redirect is off.
 */
const WRITE_META = { suppressForbiddenRedirect: true };

const dayPath = (businessDate: string) =>
  `${DAILY_EXPENSES_PATH}/${encodeURIComponent(businessDate)}`;

const documentPath = (uuid: string) =>
  `${DAILY_EXPENSES_PATH}/${encodeURIComponent(uuid)}`;

export const getDailyExpenseCategories = () =>
  axios.get<ApiResponse<ExpenseCategoryList>>(EXPENSE_CATEGORIES_PATH, {
    params: { entry_type: DAILY_ENTRY_TYPE },
  });

/** S2 list: every day of the month the branch ran or has a document. */
export const getDailyExpenseMonth = (month: string) =>
  axios.get<ApiResponse<DailyExpenseMonth>>(DAILY_EXPENSES_PATH, {
    params: { month },
  });

/** One day. Head office names the branch; a branch reads its own and sends none. */
export const getDailyExpenseDay = (businessDate: string, branchCode?: string) =>
  axios.get<ApiResponse<DailyExpenseDay>>(dayPath(businessDate), {
    params: branchCode ? { branch_code: branchCode } : undefined,
  });

/** บันทึกร่าง — replaces every line of the day. */
export const saveDailyExpenseDraft = (
  businessDate: string,
  body: DailyExpenseSaveRequest,
) =>
  axios.put<ApiResponse<DailyExpenseDay>>(dayPath(businessDate), body, {
    meta: WRITE_META,
  });

/** ส่งให้ HO — submits what was saved last. */
export const submitDailyExpense = (businessDate: string) =>
  axios.post<ApiResponse<DailyExpenseDay>>(`${dayPath(businessDate)}/submit`, null, {
    meta: WRITE_META,
  });

/** ไม่มีค่าใช้จ่ายวันนี้ — clears the lines and submits a zero document. */
export const submitDailyExpenseNoExpense = (businessDate: string) =>
  axios.post<ApiResponse<DailyExpenseDay>>(
    `${dayPath(businessDate)}/no-expense`,
    null,
    { meta: WRITE_META },
  );

/** S3 list and the branch × day view, from one request. */
export const getDailyExpenseReview = (params: DailyExpenseReviewParams) => {
  const search = new URLSearchParams({ start: params.start, end: params.end });
  params.branchCodes.forEach((branchCode) => search.append("branch", branchCode));
  if (params.status) search.append("status", params.status);
  return axios.get<ApiResponse<DailyExpenseReview>>(
    `${DAILY_EXPENSES_PATH}/review?${search.toString()}`,
  );
};

export const approveDailyExpense = (uuid: string) =>
  axios.post<ApiResponse<unknown>>(`${documentPath(uuid)}/approve`, null, {
    meta: WRITE_META,
  });

export const rejectDailyExpense = (uuid: string, reason: string) =>
  axios.post<ApiResponse<unknown>>(
    `${documentPath(uuid)}/reject`,
    { reason },
    { meta: WRITE_META },
  );

/** อนุมัติที่เลือก — the server skips (and names) any document whose status moved meanwhile. */
export const approveDailyExpenses = (uuids: string[]) =>
  axios.post<ApiResponse<DailyExpenseBulkApproveResult>>(
    `${DAILY_EXPENSES_PATH}/approve`,
    { uuids },
    { meta: WRITE_META },
  );
